<script setup lang="ts">
import { useRoute } from "vue-router";
import qs from "qs";

import {
  calculateReadingTime,
  formatDate,
  generateHTMLTOC,
} from "~/helpers/education";
import { EducationArticleCard, EducationArticleSmallCard } from "#components";

const $route = useRoute();
const location = useRequestURL();

const query = qs.stringify(
  {
    fields: ["title", "subTitle", "content", "publishedAt"],
    populate: {
      seo: "*",
      author: {
        fields: ["slug", "full_name", "avatar"],
        populate: {
          avatar: {
            fields: ["url"],
          },
        },
      },
      image: {
        fields: ["url"],
      },
      post_tags: {
        fields: ["slug", "name"],
      },
      related_education_articles: {
        fields: ["id", "slug", "title", "publishedAt", "content"],
        populate: {
          image: {
            fields: ["url"],
          },
        },
      },
    },
    filters: {
      slug: {
        $eq: $route.params.slug,
      },
    },
  },
  {
    encodeValuesOnly: true,
  },
);

const { data: post } = useCustomFetch(
  `/api/strapi-full/education-posts?${query}`,
);

const data = computed(() => post.value?.data?.[0]?.attributes);

const queryLatestArticles = qs.stringify(
  {
    fields: ["id", "slug", "title", "publishedAt", "content"],
    populate: {
      image: {
        fields: ["url", "name"],
      },
    },
    pagination: {
      limit: 4,
    },
    sort: "createdAt:desc",
  },
  {
    encodeValuesOnly: true,
  },
);

const { data: latestArticles } = useCustomFetch(
  `/api/strapi-full/education-posts?${queryLatestArticles}`,
);

const { data: pageData } = useCustomFetch(
  "/api/strapi/education-individual-page",
);

const relatedPosts = computed(
  () => data.value?.related_education_articles?.data,
);
</script>

<template>
  <section v-if="data" id="maincontentblock" class="education-page">
    <SeoMeta v-if="data?.seo" v-bind="data?.seo" />
    <div class="container">
      <div class="row">
        <div class="col-xs-12 col-lg-8">
          <h2 class="mb-20 fz-45 fw-700">
            {{ data?.title }}
          </h2>
          <img
            :src="$getStrapiImageSrc(data.image)"
            class="img-fluid br-20 object-fit-cover m-auto d-block mb-20"
            :alt="data?.title"
          />

          <div class="d-flex justify-content-between my-2">
            <EducationAuthor
              v-if="data.author"
              :slug="data.author?.data?.attributes?.slug"
              :avatar="data.author?.data?.attributes?.avatar"
              :fullName="data.author?.data?.attributes?.full_name"
              :publishedAt="data.publishedAt"
            />

            <SocialIcons
              :text="pageData.social_share_text"
              :items="pageData.social_items || []"
              :url="location.href"
              :title="data.title"
            />
          </div>

          <div
            class="content fw-500 fz-16 styled-content"
            v-html="$mdRenderer.render(data.content)"
          ></div>
        </div>
        <div class="col-xs-12 col-lg-4">
          <div class="row">
            <div class="col-xs-12">
              <div class="edu-container-card">
                <LiveMarketData />
              </div>
            </div>
            <div class="col-xs-12">
              <div v-if="pageData" class="edu-container-card">
                <div class="d-flex justify-content-between align-items-center">
                  <h4 class="m-0">{{ pageData?.tags_title }}</h4>
                  <a href="/tags" class="view-all-link">{{
                    pageData?.tags_link_title
                  }}</a>
                </div>
                <div class="hstack gap-2 flex-wrap mt-20">
                  <NuxtLink
                    v-for="tag in data?.post_tags?.data"
                    :to="`/education-tags/${tag?.attributes?.slug}`"
                    class="btn btn-primary br-10 fw-500"
                  >
                    {{ tag.attributes.name }}
                  </NuxtLink>
                </div>

                <div
                  class="d-flex justify-content-between align-items-center mt-30 mb-20"
                >
                  <h4 class="m-0">{{ pageData?.latest_posts_title }}</h4>
                  <a href="/education-center" class="view-all-link">{{
                    pageData?.latest_posts_link_title
                  }}</a>
                </div>

                <EducationArticleSmallCard
                  v-if="latestArticles?.data"
                  v-for="latestArticle in latestArticles?.data"
                  :key="latestArticle.id"
                  :slug="latestArticle.attributes.slug"
                  :title="latestArticle.attributes.title"
                  :image="latestArticle.attributes.image"
                  :date="formatDate(latestArticle.attributes.publishedAt)"
                  :time="calculateReadingTime(latestArticle.attributes.content)"
                />
              </div>
            </div>
            <div class="col-xs-12">
              <div class="toc-card edu-container-card">
                <TableOfContent
                  :title="pageData?.content_title"
                  :content="generateHTMLTOC(data.content)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row pb-20">
        <div class="col-md-12">
          <div
            class="d-flex justify-content-between align-items-center mt-30 mb-20"
          >
            <h2 class="big-title-h2 m-0">
              {{ pageData?.related_posts_title }}
            </h2>
            <a href="/education-center" class="view-all-link">{{
              pageData?.related_posts_link_title
            }}</a>
          </div>
        </div>
        <div
          class="col-xs-12 col-sm-4 col-md-4 mb-30"
          v-for="relatedPost in relatedPosts"
        >
          <EducationArticleCard
            :key="relatedPost.id"
            :slug="relatedPost.attributes.slug"
            :title="relatedPost.attributes.title"
            :image="relatedPost.attributes.image"
            :date="formatDate(relatedPost.attributes.publishedAt)"
            :time="calculateReadingTime(relatedPost.attributes.content)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
